/// <amd-dependency path='VSS/LoaderPlugins/Css!TestManagement' />

import * as React from "react";
import { ReleaseDefinition } from "ReleaseManagement/Core/Contracts";
import { TestRunHelper } from "TestManagement/Scripts/TFS.TestManagement.TestRunHelper";
import { Build } from "TFS/Build/Contracts";
import { ExtensionService } from "VSS/Contributions/Services";
import { registerLWPComponent } from "VSS/LWP";
import { getService } from "VSS/Service";

export interface IRunTestPointsProxyProps {
    runOption: RunOptions;
    testPoints: any;
    selectedSuite: any;
    plan: any;
    bugCategoryType: string;
    ownerId: string;
    selectedDataCollectors?: string[];
    selectedBuild?: Build;
    releaseDefinition?: ReleaseDefinition;
    environmentId?: number;
    errorCallBack: () => void;
    successCallBack: () => void;
}

/**
 * Run options for web runner, test runner and automated tests.
 * This enum needs to be in sync with the one in PointsContract.d.ts
 */
const enum RunOptions {
    RunTestUsingWebRunner = 1,
    ResumeTestUsingWebRunner = 2,
    RunTestUsingTestRunner = 3,
    AutomatedTestRun = 4,
    RunTestUsingOldMTM = 5,
    RunTestUsingNewMTM = 6,
    RunTestUsingRunWithOptionsWebRunner = 7
}

// This is a proxy component to open web runner in the new web platform using LegacyComponent
class RunTestPointsProxy extends React.Component<IRunTestPointsProxyProps, {}> {
    public render(): React.ReactNode {
        return <div />;
    }
    private _testPoints = [];
    private _suite;
    private _convertTestPointsAndSuitesToLegacyDataModel(testPoints: any, selectedSuite: any): void {
        for (let i = 0; i < testPoints.length; i++) {
            let testPoint = testPoints[i];
            this._testPoints.push({
                testCaseId: testPoint.testCaseRef.id,
                configurationId: testPoint.configuration.id,
                configurationName: testPoint.configuration.name,
                testPointId: testPoint.id,
                assignedTo: testPoint.tester.id,
                mostRecentRunId: testPoint.lastRunId,
                automated: testPoint.isAutomated
            });
        }

        this._suite = {
            id: selectedSuite.id,
            revision: selectedSuite.revision,
            name: selectedSuite.name,
            requirementId: selectedSuite.requirementId,
            suiteType: selectedSuite.suiteType,
            type: parseInt(selectedSuite.suiteType),
            parentSuiteId: selectedSuite.parentSuite ? selectedSuite.parentSuite.id : null,
            queryText: selectedSuite.queryString
        };
    }

    public componentDidMount(): void {
        let selectedBuildUri = "";
        if (this.props.selectedBuild) {
            selectedBuildUri = this.props.selectedBuild.uri;
        }

        this._convertTestPointsAndSuitesToLegacyDataModel(this.props.testPoints, this.props.selectedSuite);
        if (this.props.runOption === RunOptions.RunTestUsingWebRunner) {
            if (TestRunHelper.areAllTestsAutomated(this._testPoints)) {
                const dataProviders: string[] = ["ms.vss-releaseManagement-web.release-service-data-external"];
                getService(ExtensionService)
                    .getContributions(dataProviders, true, false)
                    .then(() => {
                        TestRunHelper.runAutomatedTestsUsingTestPlanSettings(this._testPoints, this.props.plan, this.props.successCallBack);
                    });
            } else {
                TestRunHelper.validateSelectedTestPointsforRunningManually(this._testPoints).then(() => {
                    TestRunHelper.runTestPointsUsingWebRunner(
                        this._testPoints,
                        this._suite,
                        this.props.plan,
                        this.props.bugCategoryType,
                        this.props.ownerId,
                        this.props.errorCallBack,
                        selectedBuildUri
                    );
                });
            }
        } else if (this.props.runOption === RunOptions.RunTestUsingRunWithOptionsWebRunner) {
            TestRunHelper.runTestPointsUsingWebRunner(
                this._testPoints,
                this._suite,
                this.props.plan,
                this.props.bugCategoryType,
                this.props.ownerId,
                this.props.errorCallBack,
                selectedBuildUri
            );
        } else if (this.props.runOption === RunOptions.ResumeTestUsingWebRunner) {
            TestRunHelper.resumeRun(this._testPoints, this._suite, this.props.bugCategoryType, this.props.errorCallBack);
        } else if (this.props.runOption === RunOptions.RunTestUsingOldMTM) {
            TestRunHelper.runTestPointsUsingClient(this.props.plan.id, this._testPoints);
        } else if (this.props.runOption === RunOptions.RunTestUsingNewMTM) {
            TestRunHelper.runTestPointsUsingNewClient(
                this._testPoints,
                this._suite,
                this.props.plan,
                this.props.ownerId,
                selectedBuildUri,
                this.props.selectedDataCollectors,
                false,
                this.props.errorCallBack
            );
        } else if (this.props.runOption === RunOptions.AutomatedTestRun) {
            const dataProviders: string[] = [
                "ms.vss-releaseManagement-web.release-service-data-external",
                "ms.vss-releaseManagement-web.release-definitions-exists-data-provider"
            ];
            getService(ExtensionService)
                .getContributions(dataProviders, true, false)
                .then(() => {
                    TestRunHelper.startValidationAndRunAutomatedTestPoints(
                        this._testPoints,
                        this.props.selectedBuild,
                        this.props.releaseDefinition,
                        this.props.environmentId,
                        this.props.plan,
                        this.props.successCallBack
                    );
                });
        }
    }
}

registerLWPComponent("run-test-points-proxy", RunTestPointsProxy);
